import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { catchError, tap, throwError } from 'rxjs';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { RolAgenteService } from '../../core/services/rol-agente/rol-agente.service';
import {
  GetAllRolAgenteAction,
  GetRolAgenteByUuidAction,
  CreateRolAgenteAction,
  UpdateRolAgenteAction,
  DeleteRolAgenteAction,
} from './rol-agente.actions';
import { IRolAgente } from '../../core/interfaces/agents.interface';

export interface RolAgenteStateModel {
  rolAgentes: IRolAgente[];
  selectedRolAgente: IRolAgente | null;
  loading: boolean;
}

@State<RolAgenteStateModel>({
  name: 'rolAgente',
  defaults: {
    rolAgentes: [],
    selectedRolAgente: null,
    loading: false,
  },
})
@Injectable()
export class RolAgenteState {
  @Selector()
  static getAllRolAgentes(state: RolAgenteStateModel): IRolAgente[] {
    return state?.rolAgentes ?? [];
  }

  @Selector()
  static getSelectedRolAgente(state: RolAgenteStateModel): IRolAgente | null {
    return state?.selectedRolAgente ?? null;
  }

  @Selector()
  static getLoading(state: RolAgenteStateModel): boolean {
    return state?.loading ?? false;
  }

  constructor(
    private rolAgenteService: RolAgenteService,
    private dialog: MatDialog
  ) {}

  @Action(GetAllRolAgenteAction)
  getAllRolAgenteAction(ctx: StateContext<RolAgenteStateModel>) {
    ctx.patchState({ loading: true });
    return this.rolAgenteService.getAllRolAgente().pipe(
      tap(response => {
        ctx.patchState({
          rolAgentes: response,
          loading: false,
        });
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al obtener los roles de agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(GetRolAgenteByUuidAction)
  getRolAgenteByUuidAction(
    ctx: StateContext<RolAgenteStateModel>,
    { payload }: GetRolAgenteByUuidAction
  ) {
    ctx.patchState({ loading: true });
    return this.rolAgenteService.getRolAgenteByUuid(payload).pipe(
      tap(response => {
        ctx.patchState({
          selectedRolAgente: response,
          loading: false,
        });
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al obtener el rol de agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(CreateRolAgenteAction)
  createRolAgenteAction(
    ctx: StateContext<RolAgenteStateModel>,
    { payload }: CreateRolAgenteAction
  ) {
    ctx.patchState({ loading: true });
    return this.rolAgenteService.createRolAgente(payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El rol de agente se ha creado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllRolAgenteAction());
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al crear el rol de agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateRolAgenteAction)
  updateRolAgenteAction(
    ctx: StateContext<RolAgenteStateModel>,
    { payload }: UpdateRolAgenteAction
  ) {
    ctx.patchState({ loading: true });
    return this.rolAgenteService
      .updateRolAgente(payload.uuid, payload.data)
      .pipe(
        tap(() => {
          ctx.patchState({ loading: false });
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'success',
              text: '¡El rol de agente se ha actualizado de forma exitosa!',
              hiddeCancelBtn: true,
            },
          });
          ctx.dispatch(new GetAllRolAgenteAction());
        }),
        catchError(error => {
          ctx.patchState({ loading: false });
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'error',
              text: 'Ha ocurrido un error al actualizar el rol de agente',
              hiddeCancelBtn: true,
            },
          });
          return throwError(() => error);
        })
      );
  }

  @Action(DeleteRolAgenteAction)
  deleteRolAgenteAction(
    ctx: StateContext<RolAgenteStateModel>,
    { payload }: DeleteRolAgenteAction
  ) {
    ctx.patchState({ loading: true });
    return this.rolAgenteService.deleteRolAgente(payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El rol de agente se ha eliminado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllRolAgenteAction());
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al eliminar el rol de agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }
}

